/**
 * SignInForm — email + password sign-in with vault styling.
 */
import { useState } from "react";
import { LogIn, Mail, Eye, EyeOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface Props {
  onSignIn: (email: string, password: string) => Promise<void>;
  onSwitchToOtp: () => void;
  onSwitchToReset: () => void;
  loading: boolean;
  error: string | null;
}

export function SignInForm({ onSignIn, onSwitchToOtp, onSwitchToReset, loading, error }: Props) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSignIn(email, password);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="space-y-1.5">
        <Label htmlFor="signin-email" className="text-xs font-mono text-muted-foreground uppercase tracking-wider">Email</Label>
        <Input
          id="signin-email"
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="h-10"
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="signin-password" className="text-xs font-mono text-muted-foreground uppercase tracking-wider">Password</Label>
        <div className="relative">
          <Input
            id="signin-password"
            type={showPassword ? "text" : "password"}
            required
            autoComplete="current-password"
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="h-10 pr-10"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground transition-colors"
            tabIndex={-1}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>
      <Button type="submit" disabled={loading} className="w-full gap-2 h-10">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
        Sign in
      </Button>
      <div className="flex justify-between text-xs">
        <button type="button" onClick={onSwitchToOtp} className="text-primary hover:underline font-mono">
          <Mail className="w-3 h-3 inline mr-1" />
          Sign in with OTP
        </button>
        <button type="button" onClick={onSwitchToReset} className="text-muted-foreground hover:text-foreground font-mono">
          Forgot password?
        </button>
      </div>
      {error && <p className="text-sm text-destructive font-mono" role="alert">{error}</p>}
    </form>
  );
}
